import React, { useMemo, useState } from "react";
import Icon from "../../../../components/HOC/Icon";
import { Modal3 } from "../../../../components/DIsplay/Modal/Modal3";
import { capitalize } from "lodash";
import { formatMoney, getNumber } from "../../../../features/utils/formatMoney";
import EditIcon from "@mui/icons-material/Edit";
import { IconButton } from "@mui/material";
import Button1 from "../../../../components/form/Button1";
import CommonFreeTextInput from "../../../../components/common/inputs/CommonFreeTextInput";
import CommonDropdownInput from "../../../../components/common/inputs/CommonDropdownInput";

const formatBag = (bag) => {
  if (!bag || bag.available === false) return "Not included";

  const qty = Number(bag.quantity);
  const hasQty = Number.isFinite(qty) && qty > 0;
  const wtRaw = bag.weight;
  const w = wtRaw != null && wtRaw !== "" ? Number(wtRaw) : NaN;
  const hasWt = Number.isFinite(w) && w > 0;
  const unit = String(bag.unit || "").toUpperCase();

  if (unit === "PC" || (hasQty && !hasWt)) {
    return hasQty ? `${qty} PC` : "Included";
  }

  if (hasWt) {
    return hasQty ? `${qty}x${w} ${unit || "KG"}` : `${w} ${unit || "KG"}`;
  }

  return "Included";
};

const formatDateTime = (date, time) => {
  if (!date && !time) return "---";
  if (date && time) return `${date} ${time}`;
  return date || time || "---";
};

const segmentTitle = (f) => {
  return `${f?.departureLocationName}  →  ${f?.arrivalLocationName}`;
};

function travelerDisplayName(travelers, passengerIndex1Based) {
  const n = Number(passengerIndex1Based);
  if (!Number.isFinite(n) || n < 1) return "Unknown passenger";
  const t = Array.isArray(travelers) ? travelers[n - 1] : null;
  if (!t) return `Passenger ${n}`;
  const name =
    t?.name || [t?.firstName, t?.lastName].filter(Boolean).join(" ").trim();
  return name || `Passenger ${n}`;
}

export const Baggage = ({ order, updatedTicket, onUpdateTicket }) => {
  const orderData = order?.booking?.flightBooking?.at(0);
  const displayedFlights = Array.isArray(updatedTicket?.flights)
    ? updatedTicket.flights
    : orderData?.flights;
  const flights = Array.isArray(displayedFlights) ? displayedFlights : [];
  const travelers = Array.isArray(orderData?.travelers)
    ? orderData.travelers
    : [];

  const adultTravelers =
    orderData?.travelers?.filter((t) => t?.travelerType === "ADT") || [];

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedTraveler, setSelectedTraveler] = useState(null);
  const [editBaggageModal, setEditBaggageModal] = useState(null);
  const [editForm, setEditForm] = useState({
    quantity: "",
    weight: "",
    unit: "KG",
    availability: "Included",
  });

  const summary = useMemo(() => {
    const checkedSet = new Set(
      flights.map((f) => formatBag(f?.checkedBaggage)),
    );
    const carrySet = new Set(flights.map((f) => formatBag(f?.carryOnBaggage)));

    return {
      checked: checkedSet.size === 1 ? [...checkedSet][0] : "Varies by segment",
      carry: carrySet.size === 1 ? [...carrySet][0] : "Varies by segment",
    };
  }, [flights]);

  const openDetails = (traveler) => {
    setSelectedTraveler(traveler);
    setIsModalOpen(true);
  };

  const openEditBaggageModal = (flightIndex, baggageType) => {
    const bag = flights?.[flightIndex]?.[baggageType] || {};
    setEditForm({
      quantity:
        bag?.quantity != null && bag?.quantity !== ""
          ? String(bag.quantity)
          : "",
      weight:
        bag?.weight != null && bag?.weight !== "" ? String(bag.weight) : "",
      unit: String(bag?.unit || "KG").toUpperCase(),
      availability: bag?.available === false ? "Not included" : "Included",
    });
    setEditBaggageModal({ flightIndex, baggageType });
  };

  const handleSaveBaggage = () => {
    if (!editBaggageModal || !onUpdateTicket) return;
    const { flightIndex, baggageType } = editBaggageModal;
    const booking = updatedTicket || orderData;
    const bookingFlights = Array.isArray(booking?.flights)
      ? booking.flights
      : [];
    const quantityNumber =
      editForm.quantity === "" ? "" : Number(editForm.quantity);
    const weightNumber = editForm.weight === "" ? "" : Number(editForm.weight);
    const hasQuantity =
      quantityNumber !== "" &&
      Number.isFinite(quantityNumber) &&
      quantityNumber > 0;
    const hasWeight =
      weightNumber !== "" && Number.isFinite(weightNumber) && weightNumber > 0;
    const isIncluded = editForm.availability !== "Not included";

    const nextFlights = bookingFlights.map((flight, idx) => {
      if (idx !== flightIndex) return flight;
      const currentBag = flight?.[baggageType] || {};
      return {
        ...flight,
        [baggageType]: {
          ...currentBag,
          quantity: isIncluded ? (hasQuantity ? quantityNumber : 0) : 0,
          weight: isIncluded ? (hasWeight ? weightNumber : 0) : 0,
          unit: String(editForm.unit || "KG").toLowerCase(),
          available: isIncluded,
        },
      };
    });

    onUpdateTicket({ ...booking, flights: nextFlights });
    setEditBaggageModal(null);
  };

  return (
    <div className="w-full flex flex-col mt-6">
      <div className="flex items-center justify-between mb-3">
        <span className="font-bold text-xl">Baggage</span>

        {/* Desktop summary chips */}
        {/* <div className="hidden lg:flex items-center gap-3 text-sm text-pText">
          <span className="px-3 py-1 rounded-full bg-background200 border border-outline">
            Checked:{" "}
            <span className="font-medium text-primaryText">
              {summary.checked}
            </span>
          </span>
          <span className="px-3 py-1 rounded-full bg-background200 border border-outline">
            Carry-on:{" "}
            <span className="font-medium text-primaryText">
              {summary.carry}
            </span>
          </span>
        </div> */}
      </div>

      <div className="flex flex-col gap-3">
        {adultTravelers.map((t, i) => {
          const name =
            t?.name ||
            [t?.firstName, t?.lastName].filter(Boolean).join(" ") ||
            `Passenger ${i + 1}`;

          // const id = t?.documentNumber || t?.passportNumber || t?._id || "---";

          return (
            <div
              key={t?._id || i}
              className="w-full bg-white border border-outline rounded-lg p-4 shadow-sm"
            >
              <div className="flex items-start justify-between gap-3">
                <div className="flex flex-col">
                  <span className="font-semibold text-primaryText">
                    {capitalize(name)}
                  </span>
                  {/* <span className="text-xs text-pText uppercase">{id}</span> */}

                  {/* Mobile summary */}
                  {/* <div className="flex flex-wrap gap-2 mt-2 lg:hidden"> */}
                  <div className="flex flex-wrap gap-2 mt-2 ">
                    <span className="text-xs px-2 py-1 rounded-full bg-background200 border border-outline">
                      Checked:{" "}
                      <span className="font-medium">{summary.checked}</span>
                    </span>
                    <span className="text-xs px-2 py-1 rounded-full bg-background200 border border-outline">
                      Carry-on:{" "}
                      <span className="font-medium">{summary.carry}</span>
                    </span>
                  </div>
                </div>

                <button
                  onClick={() => openDetails(t)}
                  className="gap-2 text-sm flex items-center text-primary/70 hover:text-primary transition"
                >
                  Details
                  <Icon
                    icon="cuida:open-in-new-tab-outline"
                    className="!w-4 !h-4 !text-theme1"
                  />
                </button>
              </div>
            </div>
          );
        })}
      </div>

      <Modal3
        open={isModalOpen}
        setOpen={(v) => {
          setIsModalOpen(v);
          if (!v) setSelectedTraveler(null);
        }}
        title="Baggage details"
      >
        <div className="w-full p-4 lg:p-6 pb-0 lg:pb-0 max-w-[860px] max-h-[90vh] lg:max-h-[80vh] overflow-scroll scroll-hide relative">
          {/* Modal header */}
          <div className="flex flex-col gap-1 mb-4">
            <span className="text-lg font-semibold text-primaryText">
              {selectedTraveler?.name ||
                [selectedTraveler?.firstName, selectedTraveler?.lastName]
                  .filter(Boolean)
                  .join(" ") ||
                "Passenger"}
            </span>
            <span className="text-sm text-pText">
              Allowances may differ per segment. Review each leg below.
            </span>
          </div>

          {/* Segment cards */}
          <div className="flex flex-col gap-3">
            {flights.map((f, idx) => {
              const checked = formatBag(f?.checkedBaggage);
              const carry = formatBag(f?.carryOnBaggage);

              return (
                <div
                  key={f?._id || idx}
                  className="w-full rounded-lg border border-outline bg-background200 p-4"
                >
                  <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-2">
                    <div className="flex flex-col">
                      <span className="font-semibold text-primaryText">
                        {segmentTitle(f)}
                      </span>
                      <span className="text-xs text-pText mt-1">
                        {f?.airlineName || f?.airline ? (
                          <>
                            {f?.airlineName || f?.airline}
                            {f?.flightNumber ? ` • ${f.flightNumber}` : ""}
                            {f?.aircraftCode ? ` • ${f.aircraftCode}` : ""}
                            {f?.cabinClass ? ` • ${f.cabinClass}` : ""}
                          </>
                        ) : (
                          "---"
                        )}
                      </span>

                      <div className="text-xs text-pText mt-2 flex flex-wrap gap-x-4 gap-y-1">
                        <span>
                          Dep:{" "}
                          <span className="text-primaryText font-medium">
                            {formatDateTime(f?.departureDate, f?.departureTime)}
                          </span>
                        </span>
                        <span>
                          Arr:{" "}
                          <span className="text-primaryText font-medium">
                            {formatDateTime(f?.arrivalDate, f?.arrivalTime)}
                          </span>
                        </span>
                      </div>
                    </div>

                    <div className="flex gap-2 flex-wrap">
                      <span className="text-xs px-3 py-1 rounded-full bg-white border border-outline inline-flex items-center gap-1">
                        Checked:{" "}
                        <span className="font-semibold text-primaryText">
                          {checked}
                        </span>
                        <IconButton
                          size="small"
                          aria-label="edit checked baggage"
                          onClick={() =>
                            openEditBaggageModal(idx, "checkedBaggage")
                          }
                        >
                          <EditIcon fontSize="small" />
                        </IconButton>
                      </span>
                      <span className="text-xs px-3 py-1 rounded-full bg-white border border-outline inline-flex items-center gap-1">
                        Carry-on:{" "}
                        <span className="font-semibold text-primaryText">
                          {carry}
                        </span>
                        <IconButton
                          size="small"
                          aria-label="edit carry-on baggage"
                          onClick={() =>
                            openEditBaggageModal(idx, "carryOnBaggage")
                          }
                        >
                          <EditIcon fontSize="small" />
                        </IconButton>
                      </span>
                    </div>
                  </div>

                  {/* Paid / additional checked baggage — only when there are paid bags */}
                  {(f?.chargeableCheckedBags || []).length > 0 ? (
                    <div className="mt-3 pt-3 border-t border-outline/70">
                      <div className="text-xs font-semibold text-primaryText mb-2">
                        Additional checked baggage
                      </div>
                      <ul className="flex flex-col gap-2">
                        {(f.chargeableCheckedBags || []).map((b, bi) => {
                          const pNum = b?.passenger;
                          const rawName = travelerDisplayName(travelers, pNum);
                          const displayName = capitalize(rawName);
                          const qty = Number(b?.quantity ?? 0);
                          const hasQty = Number.isFinite(qty) && qty > 0;
                          const wtRaw = b?.weight ?? b?.maxWeight;
                          const wt =
                            wtRaw != null && wtRaw !== ""
                              ? Number(wtRaw)
                              : null;
                          const hasWt = Number.isFinite(wt) && wt > 0;
                          const unit = String(b?.unit || "KG").toUpperCase();

                          return (
                            <li
                              key={b?._id || b?.ref || bi}
                              className="rounded-lg bg-white border border-outline px-3 py-2.5"
                            >
                              <div className="flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between">
                                <div className="min-w-0">
                                  <span className="font-semibold text-primaryText text-sm">
                                    Extra checked bag
                                  </span>
                                  <span className="block text-xs text-pText mt-0.5">
                                    <span className="text-primaryText font-medium">
                                      {displayName}
                                    </span>
                                    {pNum != null && pNum !== "" ? (
                                      <span className="text-pText/85">
                                        {" "}
                                        · Traveler {pNum}
                                      </span>
                                    ) : null}
                                  </span>
                                </div>
                                {b?.price ? (
                                  <div className="text-sm font-semibold text-primaryText sm:text-right shrink-0">
                                    {formatMoney(getNumber(b.price))}
                                  </div>
                                ) : null}
                              </div>
                              <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-xs">
                                {hasQty ? (
                                  <span>
                                    <span className="text-pText">
                                      Quantity:{" "}
                                    </span>
                                    <span className="font-medium text-primaryText">
                                      {qty} {qty === 1 ? "bag" : "bags"}
                                    </span>
                                  </span>
                                ) : null}
                                {hasWt ? (
                                  <span>
                                    <span className="text-pText">
                                      Weight allowance:{" "}
                                    </span>
                                    <span className="font-medium text-primaryText">
                                      {wt} {unit}
                                      {hasQty && qty > 1 ? " each" : ""}
                                    </span>
                                  </span>
                                ) : null}
                              </div>
                            </li>
                          );
                        })}
                      </ul>
                    </div>
                  ) : null}
                </div>
              );
            })}
          </div>

          {/* Note */}
          <div className="mt-4 p-3 rounded-md bg-white border border-outline text-sm text-pText">
            Baggage rules and penalties can vary by segment and airline. If you
            have questions, please contact 24/7 support.
          </div>
          <div className="w-full h-10 sticky bottom-0 bg-gradient-to-t from-white to-transparent pb-10"></div>
        </div>
      </Modal3>
      <Modal3
        open={!!editBaggageModal}
        setOpen={(v) => {
          if (!v) setEditBaggageModal(null);
        }}
        title={`Edit ${
          editBaggageModal?.baggageType === "carryOnBaggage"
            ? "Carry-on"
            : "Checked"
        } baggage`}
      >
        <div className="card p-6 flex flex-col gap-4 w-full sm:w-[420px]">
          <CommonDropdownInput
            value={editForm.availability}
            onChange={(value) =>
              setEditForm((prev) => ({ ...prev, availability: value }))
            }
            label="Availability"
            placeholder="Select availability"
            options={["Included", "Not included"]}
          />
          <CommonDropdownInput
            value={editForm.unit}
            onChange={(value) =>
              setEditForm((prev) => ({ ...prev, unit: value }))
            }
            label="Unit"
            placeholder="Select unit"
            options={["KG", "LB", "PC"]}
          />
          <CommonFreeTextInput
            value={editForm.quantity}
            onChange={(value) =>
              setEditForm((prev) => ({
                ...prev,
                quantity: String(value || "").replace(/\D/g, ""),
              }))
            }
            label="Quantity"
            placeholder="e.g. 1"
          />
          <CommonFreeTextInput
            value={editForm.weight}
            onChange={(value) =>
              setEditForm((prev) => ({
                ...prev,
                weight: String(value || "").replace(/\D/g, ""),
              }))
            }
            label="Weight"
            placeholder="e.g. 23"
          />

          <div className="flex gap-3 items-center justify-end">
            <Button1
              variant="outlined"
              className="whitespace-nowrap"
              onClick={() => setEditBaggageModal(null)}
            >
              Cancel
            </Button1>
            <Button1 onClick={handleSaveBaggage}>Save baggage</Button1>
          </div>
        </div>
      </Modal3>
    </div>
  );
};
